import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CreditCard, CheckCircle2, AlertCircle, Clock, Copy, RefreshCw } from 'lucide-react';
import QRCode from 'react-qr-code';
import { toast } from 'sonner';
import { useBilling } from '../contexts/BillingContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtBRL = (n) => `R$ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('pt-BR') : '—');

const StatusPill = ({ status, overdueDays, daysToDue }) => {
  if (status === 'paid') {
    return (
      <Badge className="bg-emerald-600 text-white" data-testid="billing-status-pill">
        <CheckCircle2 size={14} className="mr-1" /> Em dia
      </Badge>
    );
  }
  if (overdueDays > 0) {
    return (
      <Badge className="bg-red-600 text-white" data-testid="billing-status-pill">
        <AlertCircle size={14} className="mr-1" /> Vencido há {overdueDays} dia(s)
      </Badge>
    );
  }
  if (daysToDue != null) {
    return (
      <Badge className="bg-amber-500 text-white" data-testid="billing-status-pill">
        <Clock size={14} className="mr-1" /> Vence em {daysToDue} dia(s)
      </Badge>
    );
  }
  return <Badge variant="outline" data-testid="billing-status-pill">Sem liberação ativa</Badge>;
};

const BillingPage = () => {
  const { status, refresh, isBlocked } = useBilling();
  const [brcode, setBrcode] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      await refresh();
      const hist = await axios.get(`${API}/billing/history`, { withCredentials: true });
      setHistory(hist.data || []);
      try {
        const qr = await axios.get(`${API}/billing/qrcode`, { withCredentials: true });
        setBrcode(qr.data);
      } catch {
        setBrcode(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = () => {
    if (!brcode?.brcode) return;
    navigator.clipboard.writeText(brcode.brcode);
    toast.success('Copia e Cola copiado!');
  };

  const sub = status?.subscription;

  return (
    <Layout>
      <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="billing-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <CreditCard /> Liberar Sistema — Smart Flow
            </h1>
            <p className="text-sm text-slate-500">
              Gerencie a liberação mensal do sistema (licença).
            </p>
          </div>
          <Button variant="outline" onClick={loadAll} disabled={loading} data-testid="billing-refresh-btn">
            <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Atualizar
          </Button>
        </div>

        {isBlocked && (
          <Card className="p-4 border-red-500 border-2 bg-red-50">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-red-600 shrink-0" />
              <div>
                <div className="font-bold text-red-700">Acesso suspenso</div>
                <div className="text-sm text-red-700">
                  A liberação do sistema está vencida há mais dias que o limite de tolerância. O sistema continuará bloqueado até a confirmação do pagamento. Use o PIX abaixo, informe uma chave de liberação manual ou contate o administrador.
                </div>
              </div>
            </div>
          </Card>
        )}

        <Card className="p-6 space-y-4" data-testid="billing-status-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-lg font-semibold">Liberação atual</div>
            <StatusPill
              status={status?.status}
              overdueDays={status?.overdue_days}
              daysToDue={status?.days_to_due}
            />
          </div>

          {sub ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-slate-500">Valor</div>
                <div className="text-xl font-bold" data-testid="billing-amount">
                  {fmtBRL(sub.amount)}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Período</div>
                <div className="font-medium">
                  {fmtDate(sub.period_start)} → {fmtDate(sub.period_end)}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Vencimento</div>
                <div className="font-medium" data-testid="billing-due-date">{fmtDate(sub.due_date)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Tolerância</div>
                <div className="font-medium">{status?.tolerance_days || 2} dia(s)</div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              Nenhuma liberação lançada. Aguarde o administrador do sistema gerar o próximo período.
            </div>
          )}
        </Card>

        {brcode?.brcode && status?.status !== 'paid' && (
          <Card className="p-6" data-testid="billing-pix-card">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="bg-white p-3 rounded-lg border">
                <QRCode value={brcode.brcode} size={200} />
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <div className="text-lg font-bold">Pague via PIX</div>
                  <div className="text-sm text-slate-500">
                    Escaneie o QR Code acima ou copie o código abaixo:
                  </div>
                </div>
                <div className="bg-slate-100 rounded p-3 font-mono text-xs break-all" data-testid="billing-brcode">
                  {brcode.brcode}
                </div>
                <Button onClick={copy} data-testid="billing-copy-brcode-btn">
                  <Copy size={14} className="mr-1" /> Copiar Copia-e-Cola
                </Button>
                <div className="text-xs text-slate-500">
                  Beneficiário: <strong>{brcode.pix_name}</strong> · Valor: <strong>{fmtBRL(brcode.amount)}</strong>
                </div>
                <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
                  Após o pagamento, envie o comprovante para o administrador do sistema confirmar a baixa.
                </div>
              </div>
            </div>
          </Card>
        )}

        <Card className="p-6" data-testid="billing-history-card">
          <div className="text-lg font-semibold mb-3">Histórico</div>
          {history.length === 0 ? (
            <div className="text-sm text-slate-500">Nenhum lançamento registrado.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b">
                    <th className="py-2">Período</th>
                    <th className="py-2">Vencimento</th>
                    <th className="py-2">Valor</th>
                    <th className="py-2">Pago em</th>
                    <th className="py-2">Forma</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-b" data-testid="billing-history-row">
                      <td className="py-2">{fmtDate(h.period_start)} → {fmtDate(h.period_end)}</td>
                      <td className="py-2">{fmtDate(h.due_date)}</td>
                      <td className="py-2">{fmtBRL(h.amount)}</td>
                      <td className="py-2">{fmtDate(h.paid_at)}</td>
                      <td className="py-2">{h.payment_method || '—'}</td>
                      <td className="py-2">
                        {h.status === 'paid' ? (
                          <Badge className="bg-emerald-600 text-white">Pago</Badge>
                        ) : (
                          <Badge variant="outline">{h.status}</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default BillingPage;
